import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { IWeissmanModalComponent } from '../../WeissmanModalService';
import { FilingBatchInfoComponent } from '../Filing-Batch-Details/Filing-Batch-Info/filingBatchInfo.component';
import { FilingBatchRepository } from '../../Repositories';
import { ModalWrapperComponent } from '../../../UI-Lib/Modal-Wrapper/modalWrapper.component';

export interface FilingBatchCreateModalParams {
    companyId: number;
}

/**
 * Compact create-only modal for a new filing batch. Hosts the shared `FilingBatchInfoComponent`
 * form contents inside `ws-modal-wrapper` with a standard Save/Cancel footer. No progress
 * indicator, expand/collapse, or delete controls are shown - those are details-page-only concerns.
 *
 * Cancel/close/Escape abandon the new batch immediately (no unsaved-changes prompt). A failed
 * save leaves the modal open with the entered values intact so global HTTP error handling can
 * surface the failure and the user can retry.
 */
@Component({
    selector: 'filing-batch-create-modal',
    templateUrl: './filingBatchCreateModal.component.html',
    styleUrls: ['./filingBatchCreateModal.component.scss'],
    standalone: true,
    imports: [CommonModule, ModalWrapperComponent, FilingBatchInfoComponent]
})
export class FilingBatchCreateModalComponent implements IWeissmanModalComponent<FilingBatchCreateModalParams, Compliance.FilingBatchModel>, OnInit {
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _filingBatchRepository: FilingBatchRepository,
    ) {}

    params: FilingBatchCreateModalParams;
    result: Compliance.FilingBatchModel;

    filingBatch: Compliance.FilingBatchModel;

    /** True while the create request is in flight; disables Save and keeps the modal open. */
    readonly submitting = signal(false);

    /** Tracks the shared form's validity, fed by the child's `formValidityChanged` output so we don't need to reach into it via ViewChild. */
    readonly formValid = signal(false);

    /**
     * Latest form-derived model, fed by the child's `filingBatchChange` output so we don't need
     * to reach into it to pull the model when saving.
     */
    private _workingModel: Compliance.FilingBatchModel;

    ngOnInit(): void {
        this.filingBatch = {
            companyId: this.params.companyId,
            filingBatchId: 0,
            processStatus: Compliance.FilingBatchProcessStatusEnum.NotStarted,
            description: null,
            dueDate: null,
            filingTypeId: null,
            stateId: null,
            taxYear: null,
            rowVersion: null,
            // "Prior Return" is the default Change Detection source for new filing batches; Company Asset List is an explicit opt-in.
            getPriorReturnFromAssetList: false,
            reportNBVInsteadOfFactoredCost: false,
            returnCostReportingDisplayId: Compliance.ReturnCostReportingDisplayEnum.Both
        } as Compliance.FilingBatchModel;

        this._workingModel = this.filingBatch;
    }

    get disableSave(): boolean {
        return this.submitting() || !this.formValid();
    }

    onFilingBatchChange(model: Compliance.FilingBatchModel): void {
        this._workingModel = model;
    }

    async save(): Promise<void> {
        if (!this._workingModel || !this.formValid() || this.submitting()) {
            return;
        }

        this.submitting.set(true);

        try {
            this.result = await lastValueFrom(this._filingBatchRepository.create(this._workingModel));
            this._bsModalRef.hide();
        } finally {
            this.submitting.set(false);
        }
    }

    cancel(): void {
        this._bsModalRef.hide();
    }
}


